import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { useGooglePlacesAutocomplete } from '~/hooks/useGooglePlacesAutocomplete';
import { FieldError, FieldTitle } from '~/styles/forms/fieldFeedback';
import { FormField, StyledInput } from '~/styles/forms/formElements';
import { SearchSelect } from '~/styles/forms/SearchSelect';
import { SelectGroup } from '~/styles/forms/styledSelectGroup';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';
import type {
  LanguageCode,
  StudentType,
  TargetType,
} from '~/types/congregationGroupTypes';

type GeoTargetItem = components['schemas']['GeoTargetItem'];

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/web-ads-targets/add',
)({
  validateSearch: (search: Record<string, unknown>) => ({
    id: typeof search.id === 'number' ? search.id : undefined,
  }),
  component: RouteComponent,
});

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

const RADIUS_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 50] as const;

// Endonym + English name
// Matches the .ORG site convention.
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English (English)' },
  { value: 'es', label: 'Español (Spanish)' },
  { value: 'fr', label: 'Français (French)' },
  { value: 'pt-br', label: 'Português (Portuguese)' },
] as const;

const METERS_PER_MILE = 1609;

// Map an existing API target onto the form's initial values: meters -> miles,
// canonical enum forms ("EN", "PT_BR", "ONLINE") back to the form's
// lowercase-dash form, and RANGE -> the two target-type choices the form offers.
function toFormInitial(target: GeoTargetItem): {
  locationName: string;
  center: { lat: number; lng: number };
  // Display-only first entry of each area array. A migrated target can hold
  // several localities and an administrative_area_level_2 this form has no
  // field for, so these are never what gets saved back — an edit that doesn't
  // re-pick a place resends the target's arrays verbatim instead (see the
  // submit handler).
  address: { city: string; state: string; country: string };
  radiusMiles: number;
  languages: LanguageCode[];
  studentType: StudentType;
  targetType: TargetType;
} {
  return {
    locationName: target.title,
    center: { lat: target.lat, lng: target.lng },
    address: {
      city: target.locality?.[0] ?? '',
      state: target.administrative_area_level_1?.[0] ?? '',
      country: target.country?.[0] ?? '',
    },
    radiusMiles: target.radius
      ? Math.round(target.radius / METERS_PER_MILE)
      : 5,
    languages: target.languages.map(
      (l) => l.toLowerCase().replace('_', '-') as LanguageCode,
    ),
    studentType: target.student_type.toLowerCase() as StudentType,
    targetType: target.range === 'RADIUS' ? 'in-radius' : 'whole-region',
  };
}

function RouteComponent() {
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/web-ads-targets/add',
  });
  const { id } = Route.useSearch();

  // Resolve the numeric group id (endpoints are keyed by id; the route only
  // carries the slug).
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  // Editing carries only the target id (see the index page's edit link), so load
  // the group's targets and find it to prefill. The list is usually already
  // cached from the page we navigated from, so this resolves without a flash.
  const { data: targets, isLoading: targetsLoading } =
    api.listGroupGeoTargets.useQuery(
      { path: { group_id: groupId ?? 0 } },
      { enabled: !!groupId && !!id },
    );
  const existing = id ? targets?.find((tt) => tt.id === id) : undefined;

  // Hold the form until the target (if any) is resolved, so its fields mount
  // with the right initial values — they're seeded once, at mount.
  if (id && (!groupId || targetsLoading)) {
    return null;
  }

  return (
    <TargetForm
      key={existing?.id ?? 'new'}
      groupName={groupName}
      groupId={groupId}
      existing={existing}
    />
  );
}

interface TargetFormProps {
  groupName: string;
  groupId: number | undefined;
  existing: GeoTargetItem | undefined;
}

function TargetForm({ groupName, groupId, existing }: TargetFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const initial = existing ? toFormInitial(existing) : undefined;
  const isEditing = !!existing;

  const { mutateAsync: createGeoTarget, isPending: creating } =
    api.createGeoTarget.useMutation();
  const { mutateAsync: updateGeoTarget, isPending: updating } =
    api.updateGeoTarget.useMutation();
  const isPending = creating || updating;

  const [city, setCity] = useState(initial?.locationName ?? '');
  // 'required' — nothing picked at all; 'place' — a whole-region target with no
  // area data, so a place has to be picked before it can be saved.
  const [cityError, setCityError] = useState<'required' | 'place' | null>(null);
  const [selectedCityDisplay, setSelectedCityDisplay] = useState(
    initial?.locationName ?? '',
  );
  // The selected place's coordinates + address parts, captured from the Google
  // Places pick. Required to persist (geo_targets stores lat/lng). Prefilled
  // from an existing target's center and area parts when editing, so saving
  // without re-picking preserves the whole-region selection; null until a place
  // is chosen when creating.
  const [place, setPlace] = useState<{
    lat: number;
    lng: number;
    city: string;
    state: string;
    country: string;
  } | null>(
    initial
      ? {
          lat: initial.center.lat,
          lng: initial.center.lng,
          city: initial.address.city,
          state: initial.address.state,
          country: initial.address.country,
        }
      : null,
  );
  const [languages, setLanguages] = useState<LanguageCode[]>(
    initial?.languages ?? [],
  );
  const [studentType, setStudentType] = useState<StudentType>(
    initial?.studentType ?? 'all',
  );
  const [targetType, setTargetType] = useState<TargetType>(
    initial?.targetType ?? 'in-radius',
  );
  const [radius, setRadius] = useState(String(initial?.radiusMiles ?? 5));
  // Whether a place was picked from the autocomplete in this session. Until it
  // is, an existing target's area arrays are resent verbatim rather than rebuilt
  // from `place` — rebuilding would collapse a multi-value array to its first
  // entry and drop administrative_area_level_2 entirely.
  const [placePicked, setPlacePicked] = useState(false);

  const { inputRef } = useGooglePlacesAutocomplete({
    apiKey: GOOGLE_MAPS_API_KEY,
    onPlaceSelect: (address) => {
      const abbreviatedCountry =
        // Shows USA instead of just US to match original website
        address.countryCode === 'US'
          ? 'USA'
          : address.countryCode || address.country;

      setCity(
        [address.city, address.stateCode || address.state, abbreviatedCountry]
          .filter(Boolean)
          .join(', '),
      );
      setSelectedCityDisplay(
        [address.city, address.state, address.countryName || address.country]
          .filter(Boolean)
          .join(', '),
      );
      setPlace({
        lat: address.lat,
        lng: address.lng,
        city: address.city,
        state: address.state,
        country: address.countryName || address.country,
      });
      setPlacePicked(true);
      setCityError(null);
    },
  });

  const handleBack = () => {
    navigate({
      to: '/groups/$groupName/web-ads-targets/',
      params: { groupName },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // A place must be picked from the autocomplete so we have coordinates.
    if (!city.trim() || !place) {
      setCityError('required');
      return;
    }

    const backToList = () =>
      navigate({
        to: '/groups/$groupName/web-ads-targets/',
        params: { groupName },
      });

    if (!groupId) {
      backToList();
      return;
    }

    const isWholeRegion = targetType === 'whole-region';
    // Keep an existing whole-region target's specific range (COUNTRY,
    // AREA_LEVEL_1, …) on edit rather than collapsing it to LOCALITY; the form
    // only offers LOCALITY when creating a new whole-region target.
    const wholeRegionRange =
      existing && existing.range !== 'RADIUS' ? existing.range : 'LOCALITY';

    // The area arrays a whole-region target matches on. Editing without
    // re-picking a place resends the stored arrays untouched — a migrated
    // target can list several localities and an administrative_area_level_2
    // this form has no field for, and rebuilding from `place` would silently
    // drop both. Picking a place replaces them; a radius target has none.
    const keepStoredAreas = !!existing && !placePicked;
    const areas =
      isWholeRegion && keepStoredAreas
        ? {
            locality: existing.locality,
            administrative_area_level_1: existing.administrative_area_level_1,
            administrative_area_level_2: existing.administrative_area_level_2,
            country: existing.country,
          }
        : {
            locality: isWholeRegion && place.city ? [place.city] : null,
            administrative_area_level_1:
              isWholeRegion && place.state ? [place.state] : null,
            administrative_area_level_2: null,
            country: isWholeRegion && place.country ? [place.country] : null,
          };

    // Matching never looks at a whole-region target's coordinates, only its
    // areas, so one with no area at all can never claim a student — the API
    // rejects it with a 422. Switching a radius target over is the way to get
    // here (a radius target stores no areas), so ask for a place first.
    if (
      isWholeRegion &&
      !Object.values(areas).some((value) => value && value.length > 0)
    ) {
      setCityError('place');
      return;
    }

    // The form uses lowercase locales / values; the API takes the canonical enum
    // forms ("en" -> "EN", "pt-br" -> "PT_BR", "all" -> "ALL"). Typed to the
    // request schema so `range` stays the enum union (create and update share it).
    const body: components['schemas']['GeoTargetCreateRequest'] = {
      title: selectedCityDisplay || city,
      lat: place.lat,
      lng: place.lng,
      range: targetType === 'in-radius' ? 'RADIUS' : wholeRegionRange,
      radius_miles: targetType === 'in-radius' ? Number(radius) : null,
      languages: languages.map(
        (l) =>
          l.replace('-', '_').toUpperCase() as 'EN' | 'ES' | 'FR' | 'PT_BR',
      ),
      student_type: studentType.toUpperCase() as 'ALL' | 'POSTAL' | 'ONLINE',
      ...areas,
    };

    if (isEditing && existing) {
      await updateGeoTarget({
        path: { group_id: groupId, target_id: existing.id },
        body,
      });
    } else {
      await createGeoTarget({ path: { group_id: groupId }, body });
    }
    backToList();
  };

  const showRadiusField = targetType === 'in-radius';

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('wbs.web-targets')} />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={handleBack} />
        <form onSubmit={handleSubmit}>
          <FormField>
            <FieldTitle>{t('wbs.languages')}</FieldTitle>
            <SearchSelect
              options={LANGUAGE_OPTIONS}
              value={languages}
              onChange={(v) => setLanguages(v as LanguageCode[])}
            />
          </FormField>

          <FormField>
            {/* Translation Needed */}
            <FieldTitle htmlFor="studentType">Student Type</FieldTitle>
            <SelectGroup>
              <select
                id="studentType"
                value={studentType}
                style={{ color: 'var(--grey-900)' }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === 'all' ||
                    value === 'postal' ||
                    value === 'online'
                  ) {
                    setStudentType(value);
                  }
                }}
              >
                <option value="all">{t('lessons.all-students')}</option>
                {/* Translations Needed */}
                <option value="postal">Postal Students Only</option>
                <option value="online">Online Students Only</option>
              </select>
              <span className="icon">
                <FontAwesomeIcon icon={faCaretDown} />
              </span>
            </SelectGroup>
          </FormField>

          <FormField>
            <FieldTitle htmlFor="targetType">{t('wbs.target-type')}</FieldTitle>
            <SelectGroup>
              <select
                id="targetType"
                value={targetType}
                style={{ color: 'var(--grey-900)' }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'in-radius' || value === 'whole-region') {
                    setTargetType(value);
                  }
                }}
              >
                <option value="in-radius">{t('wbs.in-radius')}</option>
                <option value="whole-region">{t('wbs.whole-region')}</option>
              </select>
              <span className="icon">
                <FontAwesomeIcon icon={faCaretDown} />
              </span>
            </SelectGroup>
          </FormField>

          <FormField>
            <FieldTitle htmlFor="city" $hasError={!!cityError}>
              {t('wbs.city')}
            </FieldTitle>
            <StyledInput
              id="city"
              ref={inputRef}
              type="text"
              style={{ width: '100%' }}
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setSelectedCityDisplay('');
                if (e.target.value.trim()) setCityError(null);
              }}
              // Translation Needed
              placeholder={'Enter a location'}
              autoComplete="off"
            />
            {cityError && (
              <FieldError>
                {cityError === 'place'
                  ? t('wbs.please-enter-a-place')
                  : t('wbs.this-field-is-required')}
              </FieldError>
            )}
          </FormField>

          {showRadiusField ? (
            <FormField>
              <FieldTitle htmlFor="radius">{t('wbs.radius-miles')}</FieldTitle>
              <SelectGroup>
                <select
                  id="radius"
                  value={radius}
                  style={{ color: 'var(--grey-900)' }}
                  onChange={(e) => setRadius(e.target.value)}
                >
                  {RADIUS_OPTIONS.map((r) => (
                    <option key={r} value={String(r)}>
                      {r}
                    </option>
                  ))}
                </select>
                <span className="icon">
                  <FontAwesomeIcon icon={faCaretDown} />
                </span>
              </SelectGroup>
            </FormField>
          ) : (
            <FormField>
              <FieldTitle htmlFor="wholeRegionCity">
                {t('wbs.select-your-target-city')}
              </FieldTitle>
              <WholeRegionChoice>
                <WholeRegionText>
                  <WholeRegionSubtitleRow>
                    {!!selectedCityDisplay && (
                      <input
                        id="wholeRegionCity"
                        type="radio"
                        checked
                        readOnly
                      />
                    )}
                    <WholeRegionSubtitle $hasCity={!!selectedCityDisplay}>
                      {selectedCityDisplay || t('wbs.please-enter-a-place')}
                    </WholeRegionSubtitle>
                  </WholeRegionSubtitleRow>
                </WholeRegionText>
              </WholeRegionChoice>
            </FormField>
          )}

          <WBSButton wide type="submit" disabled={isPending}>
            {isEditing ? t('wbs.update') : t('actions.add')}
          </WBSButton>
        </form>
      </FormContainer>
    </>
  );
}

const WholeRegionChoice = styled.label`
  align-items: flex-start;
  cursor: default;
  display: flex;
  padding: 0;
  width: 100%;
`;

const WholeRegionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const WholeRegionSubtitle = styled.span<{ $hasCity: boolean }>`
  color: ${({ $hasCity }) => ($hasCity ? 'var(--grey-900)' : 'var(--wbs-red)')};
  font-family: var(--raleway);
  font-size: 13px;
  font-style: ${({ $hasCity }) => ($hasCity ? 'normal' : 'italic')};
`;

const WholeRegionSubtitleRow = styled.span`
  align-items: center;
  display: flex;
  gap: 8px;

  input {
    accent-color: var(--wbs-red);
    flex-shrink: 0;
    margin: 0;
  }
`;
